/**
 * Deterministic vector math for the simulation.
 *
 * ── On determinism and floating point: read this before touching the sim ───
 *
 * IEEE-754 guarantees +, -, *, / and sqrt are correctly rounded, so they are
 * bit-identical on every conforming engine. The vector ops below are built
 * only from those five operations and are safe anywhere.
 *
 * Math.sin/cos/atan2/pow/exp/log are NOT specified to be correctly rounded.
 * ECMA-262 explicitly permits implementation-defined results, and V8,
 * SpiderMonkey and JavaScriptCore do differ in the last ulp.
 *
 * WHAT WE ACTUALLY GUARANTEE, precisely:
 *
 *   The sim is bit-identical between any two V8 builds. Our server is Node
 *   (V8) and our shipping clients are Chrome/Edge (V8), so the deployed
 *   configuration is bit-identical, and §13.1's test — Node vs. headless
 *   Chrome — genuinely proves that. This is a real guarantee, not a hedge.
 *
 * WHAT WE DO NOT GUARANTEE, and why it's acceptable:
 *
 *   A Firefox or Safari client may diverge by an ulp per transcendental call.
 *   Note the §13.1 test cannot catch this — it compares V8 to V8, so it will
 *   stay green while a SpiderMonkey client drifts. Don't read that green badge
 *   as cross-engine proof.
 *
 *   It does not sink us: the netcode is server-authoritative with
 *   reconciliation (§2.1), not lockstep. The server is the only opinion that
 *   counts, and it corrects drift on the next snapshot. Sub-ulp divergence is
 *   orders of magnitude below POSITION_EPSILON (0.05m), so it will not even
 *   trigger a correction.
 *
 * The obvious fix — hand-rolled polynomial sin/cos/atan2 built only from the
 * safe five ops — was deliberately NOT taken. A correctly-rounded atan2 is
 * genuinely hard to get right, and an almost-right one silently corrupts every
 * aim vector in the game. That is a strictly worse bug than the one it fixes,
 * and it buys nothing for the engines we actually ship on. If Firefox/Safari
 * support ever becomes a requirement, the right move is a vetted library
 * (e.g. fdlibm-derived), not a bespoke polynomial. Tracked in DECISIONS.md.
 *
 * RULE: use these wrappers, not raw Math.*, for anything that feeds back into
 * WorldState. Not because they're currently different — they aren't — but so
 * that swapping in an fdlibm port later is a one-file change instead of a
 * codebase-wide audit of every Math.sin call site.
 */

import type { Vec3 } from '../sim/types.js';

// ---------------------------------------------------------------------------
// Allocation-free vector ops. (§15.7)
// Every op writes into `out` and returns it. `out` may alias any input.
// ---------------------------------------------------------------------------

export function vset(out: Vec3, x: number, y: number, z: number): Vec3 {
  out.x = x; out.y = y; out.z = z;
  return out;
}

export function vcopy(out: Vec3, a: Vec3): Vec3 {
  out.x = a.x; out.y = a.y; out.z = a.z;
  return out;
}

export function vzero(out: Vec3): Vec3 {
  out.x = 0; out.y = 0; out.z = 0;
  return out;
}

export function vadd(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  out.x = a.x + b.x; out.y = a.y + b.y; out.z = a.z + b.z;
  return out;
}

export function vsub(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  out.x = a.x - b.x; out.y = a.y - b.y; out.z = a.z - b.z;
  return out;
}

export function vscale(out: Vec3, a: Vec3, s: number): Vec3 {
  out.x = a.x * s; out.y = a.y * s; out.z = a.z * s;
  return out;
}

/** out = a + b*s — the fused form used all over the integrator. */
export function vaddScaled(out: Vec3, a: Vec3, b: Vec3, s: number): Vec3 {
  out.x = a.x + b.x * s; out.y = a.y + b.y * s; out.z = a.z + b.z * s;
  return out;
}

export function vdot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

/** out = a × b. Load-bearing for the Magnus force: F_m = S·(ω × v). (§3.2) */
export function vcross(out: Vec3, a: Vec3, b: Vec3): Vec3 {
  // Read into locals first so `out` may safely alias `a` or `b`.
  const ax = a.x, ay = a.y, az = a.z;
  const bx = b.x, by = b.y, bz = b.z;
  out.x = ay * bz - az * by;
  out.y = az * bx - ax * bz;
  out.z = ax * by - ay * bx;
  return out;
}

export function vlengthSq(a: Vec3): number {
  return a.x * a.x + a.y * a.y + a.z * a.z;
}

export function vlength(a: Vec3): number {
  return Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
}

export function vdistanceSq(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
}

export function vdistance(a: Vec3, b: Vec3): number {
  return Math.sqrt(vdistanceSq(a, b));
}

/** Normalize; a zero-length vector yields zero rather than NaN. */
export function vnormalize(out: Vec3, a: Vec3): Vec3 {
  const lenSq = a.x * a.x + a.y * a.y + a.z * a.z;
  if (lenSq === 0) return vzero(out);
  const inv = 1 / Math.sqrt(lenSq);
  out.x = a.x * inv; out.y = a.y * inv; out.z = a.z * inv;
  return out;
}

/** Clamp magnitude to `max`, preserving direction. */
export function vclampLength(out: Vec3, a: Vec3, max: number): Vec3 {
  const lenSq = a.x * a.x + a.y * a.y + a.z * a.z;
  if (lenSq <= max * max || lenSq === 0) return vcopy(out, a);
  const s = max / Math.sqrt(lenSq);
  out.x = a.x * s; out.y = a.y * s; out.z = a.z * s;
  return out;
}

export function vlerp(out: Vec3, a: Vec3, b: Vec3, t: number): Vec3 {
  out.x = a.x + (b.x - a.x) * t;
  out.y = a.y + (b.y - a.y) * t;
  out.z = a.z + (b.z - a.z) * t;
  return out;
}

/** Horizontal (XZ) length — used constantly; players live on a plane. */
export function vlengthXZ(a: Vec3): number {
  return Math.sqrt(a.x * a.x + a.z * a.z);
}

// ---------------------------------------------------------------------------
// Scalar helpers
// ---------------------------------------------------------------------------

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Inverse lerp, guarded against a degenerate range. */
export function invLerp(a: number, b: number, v: number): number {
  if (a === b) return 0;
  return (v - a) / (b - a);
}

/** Remap v from [inA,inB] to [outA,outB], clamped. */
export function remapClamped(
  v: number, inA: number, inB: number, outA: number, outB: number,
): number {
  return lerp(outA, outB, clamp01(invLerp(inA, inB, v)));
}

export function sign(v: number): number {
  return v < 0 ? -1 : v > 0 ? 1 : 0;
}

export const PI = 3.141592653589793;
export const TWO_PI = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;
export const DEG2RAD = 0.017453292519943295;
export const RAD2DEG = 57.29577951308232;

/** Wrap an angle to [-π, π]. Built only from safe ops. */
export function wrapAngle(a: number): number {
  // Avoid Math.round for large inputs; floor-based reduction is exact here.
  let x = a + PI;
  x = x - TWO_PI * Math.floor(x / TWO_PI);
  return x - PI;
}

/** Shortest signed angular difference from `from` to `to`, in [-π, π]. */
export function angleDelta(from: number, to: number): number {
  return wrapAngle(to - from);
}

/**
 * Rotate `current` toward `target` by at most `maxDelta` radians.
 * This is how TURN_RATE_MAX/MIN become momentum (§3.3).
 */
export function rotateTowards(current: number, target: number, maxDelta: number): number {
  const d = angleDelta(current, target);
  if (d > maxDelta) return wrapAngle(current + maxDelta);
  if (d < -maxDelta) return wrapAngle(current - maxDelta);
  return target;
}

// ---------------------------------------------------------------------------
// Transcendentals — the sim's single choke point for non-exact math.
//
// These are thin wrappers over Math.* today. That is a deliberate, documented
// choice (see the header note), not an oversight: bit-identical across V8,
// which is what we ship on and what CI proves. The wrappers exist so that
// swapping in an fdlibm port for Firefox/Safari support is a one-file change.
//
// USE THESE, NOT Math.*, ANYWHERE IN /sim. A lint rule enforces it.
// ---------------------------------------------------------------------------

export function dsin(x: number): number {
  return Math.sin(x);
}

export function dcos(x: number): number {
  return Math.cos(x);
}

export function dtan(x: number): number {
  return Math.tan(x);
}

/** atan2(y, x) — the aim/facing primitive. */
export function datan2(y: number, x: number): number {
  return Math.atan2(y, x);
}

export function dpow(base: number, exp: number): number {
  return Math.pow(base, exp);
}

/**
 * Fourth root — the exact case §3.2's Magnus lift coefficient needs (^0.25).
 *
 * Worth having as its own function: sqrt is IEEE-754 correctly rounded, so
 * sqrt(sqrt(x)) is bit-identical on EVERY engine, while Math.pow(x, 0.25) is
 * not. The single hottest transcendental in the ball model is therefore exactly
 * deterministic for free, on all engines, today. Use this, never dpow(x, 0.25).
 */
export function dpow025(x: number): number {
  if (x <= 0) return 0;
  return Math.sqrt(Math.sqrt(x));
}

export function dsqrt(x: number): number {
  return Math.sqrt(x);
}

// ---------------------------------------------------------------------------
// Critically-damped spring — the correct camera smoothing. (§7.2)
// Lerp never converges and feels mushy; this does. Kept here rather than in the
// client because replay/spectator tooling needs it too.
// ---------------------------------------------------------------------------

export interface SmoothDampState {
  velocity: number;
}

export function smoothDamp(
  current: number,
  target: number,
  state: SmoothDampState,
  smoothTime: number,
  dt: number,
  maxSpeed = Number.POSITIVE_INFINITY,
): number {
  const st = smoothTime < 1e-4 ? 1e-4 : smoothTime;
  const omega = 2 / st;
  const x = omega * dt;
  // Padé approximant of e^-x — the standard SmoothDamp kernel.
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);

  let change = current - target;
  const maxChange = maxSpeed * st;
  change = clamp(change, -maxChange, maxChange);
  const newTarget = current - change;

  const temp = (state.velocity + omega * change) * dt;
  state.velocity = (state.velocity - omega * temp) * exp;
  let output = newTarget + (change + temp) * exp;

  // Prevent overshoot past the target.
  if (target - current > 0 === output > target) {
    output = target;
    state.velocity = (output - target) / dt;
  }
  return output;
}
